import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RightOutlined, ToolOutlined } from '@ant-design/icons';
import { Text, Button, ConfirmModal } from '../../../components';
import { AppliancesIcon, ArrowUpIcon } from '../../../components/icons';
import { RMCard } from '../RMCard';
import { LoadableValue, useCardLoadingState, skeletonRows, SKELETON_WIDTH } from '../CardLoadingUtils';
import { useUpgradeApplianceMutation } from '../../../api';
import {
    getApplianceReadiness,
    canApplianceUpgrade,
    APPLIANCE_READINESS_STATE_INFO,
} from '../../../pages/Clouds/utils';
import { ROUTE_CONFIG } from '../../../constants/routes';
import { CLOUD_TYPES } from '../../../constants/cloudTypes';
import { useScrollMask } from '../../ScrollMask/useScrollMask';
import { useNotificationContext } from '../../../providers';
import classNames from 'classnames';

import './MigrationAppliancesCard.scss';

const SKELETON_ROW_COUNT = 4;

const getCloudTargetLabel = (type) => CLOUD_TYPES.find((t) => t.value === type)?.label ?? type;
const CellLabel = ({ loading, children }) => !loading && <span className="cell-label">{children}</span>;

export const MigrationAppliancesCard = ({ appliances = [], queryState }) => {
    const { loading, isError, disabled } = useCardLoadingState(queryState);
    const [upgradeAppliance] = useUpgradeApplianceMutation();
    const { notifySuccess, notifyError } = useNotificationContext();
    const navigate = useNavigate();
    const [showConfirmUpgradeAppliance, setShowConfirmUpgradeAppliance] = useState(false);
    const [selectedUpgradeAppliance, setSelectedUpgradeAppliance] = useState(null);
    const [scrollRef, maskClass] = useScrollMask([appliances.length]);

    const applianceCellClassName = classNames('appliance-cell', { 'skeleton-cell': loading });
    const applianceRows = loading ? skeletonRows(SKELETON_ROW_COUNT) : appliances;

    return (
        <>
            <RMCard className="migration-appliances-card" showMoreIcon={false} isError={isError}>
                <header>
                    <Text size="large" color="secondary">
                        Migration Appliances
                    </Text>
                </header>
                <div className="landing-zones-body">
                    <div className="landing-zones-left">
                        <div className="landing-zones-count-section">
                            <span className="landing-zones-count-label">
                                <AppliancesIcon className="landing-zones-icon" />
                                <Text size="small" color="muted" bold>
                                    Appliances
                                </Text>
                            </span>
                            <LoadableValue
                                isLoading={loading}
                                width={SKELETON_WIDTH.medium}
                                className="landing-zones-count"
                            >
                                <Text size="extraLarge" xxlBold color="primaryBlue" className="landing-zones-count">
                                    {appliances.length}
                                </Text>
                            </LoadableValue>
                        </div>
                        <Link to={ROUTE_CONFIG.clouds.path}>
                            <Button type="primary" disabled={disabled}>
                                View Appliances <RightOutlined />
                            </Button>
                        </Link>
                    </div>
                    <div ref={scrollRef} className={classNames('landing-zones-right', maskClass, 'scrollbar-visible')}>
                        {applianceRows.map((item, idx) => (
                            <div className="appliance-row" key={loading ? idx : item.id}>
                                <div className="appliance-cell appliance-name">
                                    <span className="cell-value">
                                        <LoadableValue isLoading={loading} width={SKELETON_WIDTH.large}>
                                            <ToolOutlined className="row-icon" />
                                            <Link
                                                to={ROUTE_CONFIG.clouds.path}
                                                state={{ applianceId: item.id }}
                                                className="appliance-name-link"
                                            >
                                                {item.name}
                                            </Link>
                                        </LoadableValue>
                                    </span>
                                </div>
                                <div className={applianceCellClassName}>
                                    <CellLabel loading={loading}>Cloud Target</CellLabel>
                                    <span className="cell-value">
                                        <LoadableValue isLoading={loading} width={SKELETON_WIDTH.medium}>
                                            <Text size="small" extraBold color="muted">
                                                {getCloudTargetLabel(item.type)}
                                            </Text>
                                        </LoadableValue>
                                    </span>
                                </div>
                                <div className={applianceCellClassName}>
                                    <CellLabel loading={loading}>Readiness</CellLabel>
                                    <span className="cell-value">
                                        <LoadableValue isLoading={loading} width={SKELETON_WIDTH.small}>
                                            {getApplianceReadiness(item, APPLIANCE_READINESS_STATE_INFO)}
                                        </LoadableValue>
                                    </span>
                                </div>
                                <div className={applianceCellClassName}>
                                    <CellLabel loading={loading}>Version</CellLabel>
                                    <span className="cell-value">
                                        <LoadableValue isLoading={loading} width={SKELETON_WIDTH.medium}>
                                            <Text size="small" extraBold color="muted">
                                                {item.appliance?.appliance_version_num || '—'}
                                            </Text>
                                            {canApplianceUpgrade(item) && <ArrowUpIcon className="upgrade-icon" />}
                                        </LoadableValue>
                                    </span>
                                </div>
                                <div className="appliance-cell upgrade-cell">
                                    {!disabled && canApplianceUpgrade(item) && (
                                        <span
                                            className="upgrade-link"
                                            onClick={() => {
                                                setSelectedUpgradeAppliance(item);
                                                setShowConfirmUpgradeAppliance(true);
                                            }}
                                        >
                                            Upgrade <RightOutlined />
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </RMCard>
            <ConfirmModal
                title="Upgrade Migration Appliance"
                open={showConfirmUpgradeAppliance}
                onCancel={() => setShowConfirmUpgradeAppliance(false)}
                onContinue={async () => {
                    const rowId = selectedUpgradeAppliance?.id;
                    setShowConfirmUpgradeAppliance(false);
                    await upgradeAppliance({ applianceId: selectedUpgradeAppliance?.appliance?.id })
                        .unwrap()
                        .then(() => {
                            navigate(ROUTE_CONFIG.clouds.path, { state: { selectApplianceId: rowId } });
                            notifySuccess({ message: 'Success', description: 'Appliance upgrade is in progress' });
                        })
                        .catch((error) =>
                            notifyError({
                                message: 'Error',
                                description: `Error while upgrading appliance. ${error.message}`,
                            }),
                        );
                }}
                ghostCancel
            >
                Upgrade <strong>{selectedUpgradeAppliance?.name}</strong> to the latest version?
            </ConfirmModal>
        </>
    );
};
